import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import { DataGrid } from "@mui/x-data-grid";
import { Localize } from "../../../app/lang/langs";
import styles from "../Dashboard.module.css";
import moment from "moment";
import NewsModal from "./NewsModal";
import addIcon from "../../../assets/images/add.png";
import addIconFocus from "../../../assets/images/addFocus.png";
import ImageButton from "../../buttons/ImageButton";
import { checkPermission, getNewsCategoryColor } from "../../../app/utils/helpers";
import { config, NEWS_CATEGORY_LANG_KEYS } from "../../../app/utils/config";
import AddNewsModal from "./AddNewsModal";
import deleteIcon from "../../../assets/images/delete.png";
import deleteIconFocus from "../../../assets/images/delete-orange.png";
import { confirmDialog } from "primereact/confirmdialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { deleteNews, setNewsList } from "../../../containers/dashboard/dashboardSlice";
import NewsViewersDialog from "./NewsViewersDialog";
import NewsAudienceChips from "./NewsAudienceChips";

function NewsCategoryChip({ category, size = "small" }) {
    if (!category) return null
    const { color, background, border } = getNewsCategoryColor(category)
    return (
        <Chip
            label={Localize(NEWS_CATEGORY_LANG_KEYS[category] || category)}
            size={size}
            sx={{
                fontWeight: 600,
                fontSize: size === "small" ? "0.7rem" : "0.75rem",
                color,
                backgroundColor: background,
                border: border ? `1px solid ${border}` : "none",
                maxWidth: "100%",
                "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                },
            }}
        />
    )
}

// Outlook-style importance marker: a red "!" in a solid circle rather than a labeled tag,
// so it reads at a glance without competing with the category chip for space.
function NewsMandatoryMark({ mandatory, size = 16 }) {
    if (!mandatory || Number(mandatory) !== 1) return null
    return (
        <Box
            title={Localize("NEWS_MANDATORY_TAG")}
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                backgroundColor: "#d32f2f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <PriorityHighRoundedIcon sx={{ fontSize: size * 0.75, color: "#fff" }} />
        </Box>
    )
}

export default function NewsTable({
    showHeader = true,
    showAddButton = false,
    tableHeight = 260,
}) {

    const dispatch = useDispatch();
    const isMobile = useSelector((state) => state.settings.isMobile);
    const mobile = isMobile === 2;
    const newsList = useSelector((state) => state.dashboard.news)
    const [selectedNews, setSelectedNews] = useState(null)
    const [visible, setVisible] = useState(false)
    // The post whose read receipts are being shown, or null. Held here rather than inside
    // the row so the dialog survives the grid re-rendering its cells.
    const [viewersNews, setViewersNews] = useState(null)
    // Read receipts and the audience column are for the people who publish — the same scope
    // the delete action and the backend route check.
    const canManageNews = checkPermission(config.scopes.news, config.permissionTypes.edit)

    const onNewsSelect = (row) => {
        setSelectedNews(row)
    }

    const rowClassName = (row) => {
        const isSeen = row.seen;
        return isSeen ? "" : "news-row-unread";
    }

    // The last-column action: who has read this post. Sits next to the delete button and is
    // gated on the same permission — a collab who cannot publish never sees it.
    const viewersNewsBody = (row) => {
        if (!canManageNews) return null
        return (
            <Tooltip title={Localize("NEWS_VIEWERS")}>
                <IconButton
                    size="small"
                    onClick={() => setViewersNews(row)}
                    sx={{
                        color: "#B06000",
                        "&:hover": { backgroundColor: "rgba(255, 144, 21, 0.12)" },
                    }}
                >
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
        )
    }

    const deleteNewsBody = (row) => {
        if (canManageNews)
            return (<ImageButton
                tooltip={Localize("DELETE_NEWS")}
                image={deleteIcon}
                imageFocus={deleteIconFocus}
                onClick={() => { confirmDeleteNotification(row.id) }}
                active={true}
                width={32}
            />)
    }

    const confirmDeleteNotification = (newsId) => {

        const accept = async (newsId) => {
            const response = await dispatch(deleteNews({ newsId: newsId })).unwrap();
            if (response && response.responseData && response.responseData.status) {
                dispatch(setNewsList(newsList.filter(n => n.id !== newsId)))
            }
        }

        const reject = () => { }
        confirmDialog({
            message: Localize("ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_NEWS"),
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: () => accept(newsId),
            reject
        })
    }

    const rows = useMemo(() => {
        if (!newsList || !newsList.length) return [];
        return newsList.map((news, idx) => ({
            id: news.id ?? idx,
            title: news.title || "",
            message: news.message || "",
            category: news.category || "",
            mandatory: news.mandatory,
            audience: news.audience,
            created_by: news.created_by || "",
            created_at: news.created_at,
            raw: news,
        }));
    }, [newsList]);

    const columns = useMemo(
        () => mobile ? [
            {
                field: "title",
                headerName: Localize("Title"),
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        lineHeight: 1.3,
                        padding: "6px 0",
                        overflow: "hidden",
                        gap: "2px",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
                            <NewsMandatoryMark mandatory={params.row.mandatory} />
                            <span style={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}>
                                {params.value}
                            </span>
                            <NewsCategoryChip category={params.row.category} />
                        </div>
                        <span style={{
                            fontSize: "0.75rem",
                            color: "#888",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}>
                            {params.row.message}
                        </span>
                    </div>
                ),
            },
            {
                field: "created_at",
                headerName: Localize("created_at"),
                width: 100,
                valueFormatter: (value) =>
                    value ? moment(value).format("DD/MM/YY") : "",
            },
            {
                field: "actions",
                headerName: "",
                sortable: false,
                filterable: false,
                width: canManageNews ? 88 : 48,
                renderCell: (params) => (
                    <div
                        onClick={(event) => event.stopPropagation()}
                        style={{ display: "flex", alignItems: "center" }}
                    >
                        {viewersNewsBody(params.row.raw)}
                        {deleteNewsBody(params.row.raw)}
                    </div>
                ),
            },
        ] : [
            {
                field: "title",
                headerName: Localize("Title"),
                flex: 1,
                minWidth: 140,
                renderCell: (params) => (
                    <div
                        style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}
                        title={params.value}
                    >
                        <NewsMandatoryMark mandatory={params.row.mandatory} />
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {params.value}
                        </span>
                    </div>
                ),
            },
            {
                field: "category",
                headerName: Localize("NEWS_CATEGORY"),
                flex: 0.8,
                minWidth: 120,
                renderCell: (params) => <NewsCategoryChip category={params.value} />,
            },
            {
                field: "message",
                headerName: Localize("Message"),
                flex: 2,
                minWidth: 220,
                renderCell: (params) => (
                    <div
                        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={params.value}
                    >
                        {params.value}
                    </div>
                ),
            },
            // Only an author needs to know who a post was addressed to; for everyone else
            // the column would be the same value on every row they can see.
            ...(canManageNews ? [{
                field: "audience",
                headerName: Localize("NEWS_AUDIENCE"),
                flex: 1,
                minWidth: 160,
                // The cell renders the targets, not the stored 'all'/'custom' value, so
                // sorting and filtering on that value would say nothing about what is shown.
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <div style={{ display: "flex", alignItems: "center", height: "100%", overflow: "hidden" }}>
                        <NewsAudienceChips news={params.row.raw} />
                    </div>
                ),
            }] : []),
            {
                field: "created_by",
                headerName: Localize("created_by"),
                flex: 1,
                minWidth: 140,
            },
            {
                field: "created_at",
                headerName: Localize("created_at"),
                flex: 1,
                minWidth: 160,
                valueFormatter: (value) =>
                    value ? moment(value).format("DD/MM/YYYY HH:mm") : "",
            },
            {
                field: "actions",
                headerName: "",
                sortable: false,
                filterable: false,
                width: canManageNews ? 104 : 64,
                renderCell: (params) => (
                    <div
                        onClick={(event) => event.stopPropagation()}
                        style={{ display: "flex", alignItems: "center", gap: "2px" }}
                    >
                        {viewersNewsBody(params.row.raw)}
                        {deleteNewsBody(params.row.raw)}
                    </div>
                ),
            },
        ],
        [deleteNewsBody, mobile, canManageNews]
    );

    return (
        <div
            className={styles.vcompCard}
            style={{
                background: "rgba(255, 255, 255, 0.72)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                borderRadius: mobile ? "8px" : "16px",
                border: mobile ? "none" : "1px solid rgba(255, 144, 21, 0.08)",
                boxShadow: mobile ? "none" : "0 4px 24px rgba(255, 144, 21, 0.06), 0 1px 6px rgba(0, 0, 0, 0.04)",
                padding: mobile ? "4px" : "16px",
                transition: "box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
        >
            {(showHeader || showAddButton) && (
                <Box
                    className="d-flex justify-content-between align-items-center"
                    sx={{ mb: "12px" }}
                >
                    {showHeader ? (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                fontSize: "0.95rem",
                                color: "#2d2a26",
                            }}
                        >
                            {Localize("NEWS")}
                        </Typography>
                    ) : (
                        <Box />
                    )}
                    {showAddButton && checkPermission(config.scopes.news, config.permissionTypes.edit) && (
                        <ImageButton
                            tooltip={Localize("ADD_NEWS")}
                            image={addIcon}
                            imageFocus={addIconFocus}
                            onClick={() => { setVisible(true) }}
                            active={true}
                            width={32}
                        />
                    )}
                </Box>
            )}
            <Box sx={{ height: tableHeight, width: "100%" }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    density={"standard"}
                    onRowClick={(params) => onNewsSelect(params.row.raw)}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    }}
                    getRowClassName={(params) => rowClassName(params.row.raw)}
                    sx={{
                        border: mobile ? "none" : "1px solid rgba(255, 144, 21, 0.08)",
                        borderRadius: mobile ? "8px" : "14px",
                        backgroundColor: "rgba(255, 255, 255, 0.60)",
                        backdropFilter: "blur(10px)",
                        overflow: "hidden",
                        fontSize: mobile ? "0.8rem" : "0.875rem",
                        fontWeight: 400,
                        letterSpacing: "0.01em",

                        /* Unread row pulse animation */
                        "@keyframes newsRowPulse": {
                            "0%": {
                                backgroundColor: "rgba(255, 193, 7, 0.16)",
                                boxShadow: "inset 4px 0 0 rgba(255, 193, 7, 0.9)",
                            },
                            "50%": {
                                backgroundColor: "rgba(255, 193, 7, 0.05)",
                                boxShadow: "inset 4px 0 0 rgba(255, 193, 7, 0.5)",
                            },
                            "100%": {
                                backgroundColor: "rgba(255, 193, 7, 0.10)",
                                boxShadow: "inset 4px 0 0 rgba(255, 193, 7, 0.7)",
                            },
                        },
                        "& .news-row-unread": {
                            backgroundColor: mobile ? "rgba(255, 144, 21, 0.10)" : "rgba(255, 193, 7, 0.10)",
                            boxShadow: mobile ? "inset 4px 0 0 #FF9015" : "inset 4px 0 0 rgba(255, 193, 7, 0.7)",
                            animation: "newsRowPulse 2.2s ease-in-out 0s 2",
                            fontWeight: mobile ? 600 : undefined,
                        },
                        "& .news-row-unread:hover": {
                            backgroundColor: mobile ? "rgba(255, 144, 21, 0.16) !important" : "rgba(255, 193, 7, 0.14) !important",
                        },

                        /* Column headers */
                        "& .MuiDataGrid-columnHeaders": {
                            background: "linear-gradient(135deg, rgba(255, 144, 21, 0.05) 0%, rgba(255, 193, 0, 0.03) 100%)",
                            borderBottom: "1px solid rgba(255, 144, 21, 0.10)",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            color: "#6b6560",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 600,
                        },
                        "& .MuiDataGrid-columnSeparator": {
                            visibility: "hidden",
                        },

                        /* Cells */
                        "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                            color: "#3a3632",
                            transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                        },
                        "& .MuiDataGrid-cell:focus": {
                            outline: "none",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                            outline: "none",
                        },

                        /* Rows */
                        "& .MuiDataGrid-row": {
                            cursor: "pointer",
                            transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 180ms cubic-bezier(0.4, 0, 0.2, 1)",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "rgba(255, 144, 21, 0.05) !important",
                        },

                        /* Footer / pagination */
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "1px solid rgba(255, 144, 21, 0.08)",
                            background: "rgba(255, 255, 255, 0.40)",
                        },
                        "& .MuiTablePagination-root": {
                            color: "#6b6560",
                            fontSize: "0.8rem",
                        },

                        /* Scrollbar */
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                            width: "6px",
                            height: "6px",
                        },
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                            borderRadius: "3px",
                            background: "rgba(255, 144, 21, 0.18)",
                        },
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                    }}
                />
            </Box>
            <NewsModal selectedNews={selectedNews} onHide={() => setSelectedNews(null)} />
            <AddNewsModal visible={visible} setVisible={setVisible} />
            {canManageNews && (
                <NewsViewersDialog
                    newsId={viewersNews && viewersNews.id}
                    title={viewersNews && viewersNews.title}
                    createdAt={viewersNews && viewersNews.created_at}
                    open={Boolean(viewersNews)}
                    onClose={() => setViewersNews(null)}
                />
            )}
        </div>
    );
}
